import { useState } from "react";
import { useParams } from "react-router-dom";

import { FilterList } from "@mui/icons-material";
import { Box, Button, FormControlLabel, Grid, Stack, Switch } from "@mui/material";

import { ProgrammeSummary } from "../components/ProgrammeSummary";
import { DeliveryPerformance } from "../components/DeliveryPerformance";
import { RisksAndIssues } from "../components/RisksAndIssues";
import { MonthlySpendTrendChart } from "../components/MonthlySpendTrendChart";
import { VarianceAnalysisChart } from "../components/VarianceAnalysisChart";
import { AggregatedGateView } from "../components/AggregatedGateView";

import { useProgrammeSummary } from "../hooks/useProgrammeSummary";
import { useDeliveryPerformance } from "../hooks/useDeliveryPerformance";
import { usePhaseCounts } from "../hooks/usePhaseCounts";
import { useRisks } from "../hooks/useRisks";
import { useMonthlySpendTrend } from "../hooks/useMonthlySpendTrend";
import { useVarianceAnalysis } from "../hooks/useVarianceAnalysis";

import { DataLayout, DrawerControl, FilterSideBar } from "../../../shared/components";
import { useFilterContext } from "../../../shared/context";

export const ProgrammeDashboard = () => {

  const { portfolioId } = useParams();
  const [isSwitched, setIsSwitched] = useState(false);
  const [isOpenFilter, setIsOpenFilter] = useState<boolean>(false);

  const { quickFilters } = useFilterContext();

  const handleOpenFilter = () => setIsOpenFilter(true);
  const handleCloseFilter = () => setIsOpenFilter(false);

  const {
    metrics: programmeMetrics,
    isLoading: isProgrammeLoading,
    isError: isProgrammeError,
    error: programmeError
  } = useProgrammeSummary({ portfolio_id: portfolioId, quickFilters });

  const {
    metrics: deliveryMetrics,
    isLoading: isDeliveryLoading,
    isError: isDeliveryError,
    error: deliveryError,
  } = useDeliveryPerformance({ portfolio_id: portfolioId, quickFilters });

  const {
    gates,
    isLoading: isGatesLoading,
    isError: isGatesError,
    error: gatesError,
  } = usePhaseCounts({ portfolio_id: portfolioId, quickFilters });

  const {
    chartData: monthlySpendData,
    portfolioNames,
    isLoading: isLoadingMonthlySpend,
    isError: isErrorMonthlySpend,
    error: errorMonthlySpend,
  } = useMonthlySpendTrend({ portfolio_id: portfolioId, quickFilters });

  const {
    chartData: varianceData,
    isLoading: isLoadingVariance,
    isError: isErrorVariance,
    error: errorVariance,
  } = useVarianceAnalysis({ portfolio_id: portfolioId, quickFilters });

  const {
    risks: timeScheduleRisks,
    isLoading: isLoadingTimeSchedule,
    isError: isErrorTimeSchedule,
    error: errorTimeSchedule,
  } = useRisks("Time/Schedule", { portfolio_id: portfolioId, quickFilters });

  const {
    risks: costCommercialRisks,
    isLoading: isLoadingCostCommercial,
    isError: isErrorCostCommercial,
    error: errorCostCommercial,
  } = useRisks("Cost/Commercial", { portfolio_id: portfolioId, quickFilters });

  const {
    risks: safetyHseqRisks,
    isLoading: isLoadingSafetyHseq,
    isError: isErrorSafetyHseq,
    error: errorSafetyHseq,
  } = useRisks("Safety/HSEQ", { portfolio_id: portfolioId, quickFilters });

  return (
    <Box sx={{ p: 2 }} display={'flex'} flexDirection={'column'}>

      <Button
        startIcon={<FilterList />}
        variant="text"
        color="inherit"
        sx={{ marginLeft: "auto", mb: 1 }}
        onClick={handleOpenFilter}
      >
        FILTERS
      </Button>

      <Stack spacing={2}>

        <AggregatedGateView
          gates={gates}
          isLoading={isGatesLoading}
          isError={isGatesError}
          error={gatesError}
        />

        <Grid container spacing={2}>
          <Grid size={{ xs: 12, md: 6 }}>
            <ProgrammeSummary
              metrics={programmeMetrics}
              isLoading={isProgrammeLoading}
              isError={isProgrammeError}
              error={programmeError}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <DeliveryPerformance
              metrics={deliveryMetrics}
              isLoading={isDeliveryLoading}
              isError={isDeliveryError}
              error={deliveryError}
            />
          </Grid>
        </Grid>

        <RisksAndIssues
          timeScheduleRisks={timeScheduleRisks}
          costCommercialRisks={costCommercialRisks}
          safetyHseqRisks={safetyHseqRisks}
          isLoadingTimeSchedule={isLoadingTimeSchedule}
          isLoadingCostCommercial={isLoadingCostCommercial}
          isLoadingSafetyHseq={isLoadingSafetyHseq}
          isErrorTimeSchedule={isErrorTimeSchedule}
          isErrorCostCommercial={isErrorCostCommercial}
          isErrorSafetyHseq={isErrorSafetyHseq}
          errorTimeSchedule={errorTimeSchedule}
          errorCostCommercial={errorCostCommercial}
          errorSafetyHseq={errorSafetyHseq}
        />

        <DataLayout
          bgVariant="filled"
          title={isSwitched ? "Variance Analysis" : "Monthly spend trend by portfolio"} action={
            <FormControlLabel
              control={
                <Switch
                  checked={isSwitched}
                  onChange={() => setIsSwitched(!isSwitched)}
                />
              }
              label={
                isSwitched ? "Show Monthly Spend Trend" : "Show Variance Analysis"
              }
            />
          }>
          {
            !isSwitched ? (
              <MonthlySpendTrendChart
                data={monthlySpendData}
                portfolioNames={portfolioNames}
                isLoading={isLoadingMonthlySpend}
                isError={isErrorMonthlySpend}
                error={errorMonthlySpend}
              />
            ) : (
              <VarianceAnalysisChart
                data={varianceData}
                isLoading={isLoadingVariance}
                isError={isErrorVariance}
                error={errorVariance}
              />
            )
          }
        </DataLayout>

      </Stack>

      <DrawerControl
        title="Filter Records"
        subTitle="Please apply following filters to refine results"
        isOpen={isOpenFilter}
        handleClose={handleCloseFilter}
      >
        <FilterSideBar onClose={handleCloseFilter} />
      </DrawerControl>

    </Box>
  );
};
